import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import apiService from '../services/api';

const STORAGE_KEY = 'exit_intent_modal_shown';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
  padding: 2rem;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 1.5rem;
  max-width: 420px;
  width: 100%;
  padding: 2.5rem;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.2);
  position: relative;
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  background: transparent;
  border: none;
  font-size: 1.25rem;
  color: #6b7280;
  cursor: pointer;
`;

const Title = styled.h3`
  margin-top: 0;
  margin-bottom: 0.5rem;
  font-size: 1.6rem;
  color: #111827;
`;

const Text = styled.p`
  margin-bottom: 1rem;
  color: #374151;
  line-height: 1.5;
`;

const Benefits = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
  display: grid;
  gap: 0.45rem;
  color: #374151;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(15, 23, 42, 0.15);
  margin-bottom: 0.75rem;
  font-size: 1rem;
  font-family: inherit;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.95rem 1rem;
  border: none;
  border-radius: 0.8rem;
  background: linear-gradient(135deg, #ec4899 0%, #f97316 100%);
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Message = styled.p<{ success?: boolean }>`
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: ${({ success }) => (success ? '#15803d' : '#b91c1c')};
`;

const ExitIntentModal: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  const close = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    const handleMouseLeave = (event: MouseEvent) => {
      if (window.innerWidth < 768) return; // skip mobile
      if (event.clientY <= 0 && !visible) {
        setVisible(true);
        localStorage.setItem(STORAGE_KEY, 'true');
      }
    };

    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    window.addEventListener('keyup', handleEscape);
    return () => window.removeEventListener('keyup', handleEscape);
  }, [visible]);

  const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email || !validateEmail(email)) {
      setFeedback('Vänligen ange en giltig e-postadress.');
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setFeedback('');

    try {
      await apiService.sendContactMessage({
        name: email,
        email,
        subject: 'Exit intent rabatt',
        message: 'Jag vill gärna ha 10% rabatt och ta emot nyheter från Lillaflora.'
      });
      setStatus('success');
      setFeedback('Tack! Vi skickar rabatten direkt till din inbox.');
    } catch (error: any) {
      console.error('Exit intent signup failed', error);
      setStatus('error');
      setFeedback('Det gick inte att skicka just nu. Försök igen om en liten stund.');
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <Overlay onClick={close} aria-modal="true" role="dialog">
      <Card onClick={event => event.stopPropagation()}>
        <CloseButton onClick={close} aria-label="Stäng">×</CloseButton>
        <Title>Få 10% rabatt på första köpet</Title>
        <Text>
          Innan du går – ta del av ett mejlerbjudande med 10% rabatt. Vi skickar också
          inspirationsmail med nyheter och presenttips från vår butik i Kramfors.
        </Text>
        <Benefits>
          <li>Personlig service från vår lokala blomsterbutik.</li>
          <li>Inspirationstips & guider varje månad.</li>
          <li>Rabattkoden levereras direkt till mejlen.</li>
        </Benefits>
        {status === 'success' ? (
          <Message success>Rabatten är på väg till dig!</Message>
        ) : (
          <form onSubmit={handleSubmit}>
            <Input
              type="email"
              placeholder="Din e-post"
              value={email}
              onChange={event => setEmail(event.target.value)}
              aria-label="E-postadress"
              required
            />
            <SubmitButton type="submit" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Skickar…' : 'Skicka rabatten'}
            </SubmitButton>
          </form>
        )}
        {feedback && status !== 'success' && <Message>{feedback}</Message>}
      </Card>
    </Overlay>
  );
};

export default ExitIntentModal;
